import type { CertificateRequestStatus } from "@/services/certificateRequestsService";

/**
 * One question, asked in several places: can this tenant leave the Abitab
 * step?
 *
 * It is asked by three different layers, which is why it lives here rather
 * than in any of them:
 *
 *   - `canAdvance` (transitions.ts) decides whether the button is ENABLED
 *   - `resolveStep` decides where a RELOAD lands
 *   - `handleNextStep` (useOnboarding.ts) decides where the CLICK goes
 *
 * On 2026-09-30 those three disagreed. The gate and the resolver were taught
 * that the answer depends on the certificate REQUEST; the handler kept its own
 * copy keyed on the credentials, so the button enabled and then did nothing —
 * which reads as a broken app rather than as a blocked step. That was the
 * fourth instance that day of one rule living in several places with nothing
 * connecting them, so the rule got a home.
 *
 * NOTE there is a FOURTH copy, in apps/mobile (`resumeRoute`,
 * app/dgi-setup/_shared.tsx). It cannot import this: mobile depends on no
 * @fiscalo workspace package and its metro.config.js has no watchFolders or
 * nodeModulesPaths for the monorepo root, and the workspace file already
 * records Xcode build failures caused by Expo's resolution of hoisted
 * packages. Mobile therefore restates the rule, with a comment pointing here.
 * If mobile ever consumes shared-utils, this module is what should move.
 */

/**
 * A Fiscalo-managed certificate request that is still going somewhere.
 *
 * `fulfilled` counts. The certificate exists but is not yet installed, and
 * every caller here is asking "is something in motion", not "is it finished".
 * Only an absent request (nothing was ever filed) or a failed one (it stopped)
 * mean there is nothing in flight.
 */
export function isCertRequestInFlight(
  status: CertificateRequestStatus | null | undefined,
): boolean {
  // Explicitly, and FIRST: no request at all is not a request in flight.
  // Leaving null to the exhaustive default below made it fall to the
  // permissive fallback and return true — caught by this module's own test the
  // first time it ran, which is the whole argument for extracting the rule.
  if (status == null) return false;
  switch (status) {
    case "pending":
    case "in_progress":
    case "fulfilled":
      return true;
    case "failed":
      return false;
    default: {
      // Growing CertificateRequestStatus breaks the build here so a new state
      // gets classified deliberately. Falls back to true because the two
      // directions are not symmetric: true routes the tenant to a screen they
      // can walk out of, false routes them to one whose gate never clears for
      // a state it does not recognise.
      const unhandled: never = status as never;
      void unhandled;
      return true;
    }
  }
}

export interface AbitabExitState {
  /** The tenant pressed "Ya fui a Abitab". */
  acknowledgedAbitab: boolean;
  certRequestStatus: CertificateRequestStatus | null;
  /** A signing certificate is on file. */
  hasDgiCredentials: boolean;
  /** They declared they are not getting a certificate through us. */
  certificateSkipped: boolean;
  /** They chose to keep going while the certificate was still in flight. */
  waitContinued: boolean;
}

/**
 * True when nothing should hold the tenant on the Abitab step any longer.
 *
 * Three ways out, and the third is the one added on 2026-09-30: the
 * certificate arrived (or was skipped), or the tenant has been to Abitab and a
 * request is in flight — in which case the remaining work is ID Digital's and
 * ours, so waiting here buys them nothing — or they explicitly chose to carry
 * on while it comes.
 */
export function canLeaveAbitabStep(state: AbitabExitState): boolean {
  if (state.hasDgiCredentials || state.certificateSkipped) return true;
  if (state.waitContinued) return true;
  return (
    state.acknowledgedAbitab && isCertRequestInFlight(state.certRequestStatus)
  );
}
